import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, Clock, CheckCircle, XCircle, Plus, Edit2, Trash2, DollarSign, Package, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface MaintenanceTask {
  id: string;
  equipment: string;
  location: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in-progress' | 'completed';
  predictedFailure: string;
  assignedTo: string;
  cost: number;
  parts: string[];
  trafficData: {
    dailyAverage: number;
    weeklyTotal: number;
    peakHours: string;
    impactScore: number;
    trend: 'increasing' | 'stable' | 'decreasing';
  };
}

interface Vendor {
  id: string;
  name: string;
  specialty: string;
  rating: number;
  lastService: string;
  cost: number;
}

interface InventoryItem {
  id: string;
  name: string;
  quantity: number;
  reorderPoint: number;
  lastOrdered: string;
  supplier: string;
  cost: number;
}

const MaintenanceActionCenter: React.FC = () => {
  // State Management
  const [tasks, setTasks] = useState<MaintenanceTask[]>([
    {
      id: 't1',
      equipment: 'HVAC Unit 1',
      location: 'North Wing',
      priority: 'high',
      status: 'pending',
      predictedFailure: '2024-03-15',
      assignedTo: 'John Smith',
      cost: 1200,
      parts: ['Filter', 'Compressor'],
      trafficData: {
        dailyAverage: 850,
        weeklyTotal: 5950,
        peakHours: '9:00 AM - 11:00 AM',
        impactScore: 8.5,
        trend: 'increasing'
      }
    },
    {
      id: 't2',
      equipment: 'Elevator 2',
      location: 'Main Building',
      priority: 'medium',
      status: 'in-progress',
      predictedFailure: '2024-03-20',
      assignedTo: 'Sarah Johnson',
      cost: 800,
      parts: ['Motor Belt'],
      trafficData: {
        dailyAverage: 625,
        weeklyTotal: 4375,
        peakHours: '8:30 AM - 10:30 AM',
        impactScore: 6.8,
        trend: 'stable'
      }
    }
  ]);

  const [vendors, setVendors] = useState<Vendor[]>([
    {
      id: 'v1',
      name: 'TechFix Solutions',
      specialty: 'HVAC',
      rating: 4.5,
      lastService: '2024-02-01',
      cost: 5000
    },
    {
      id: 'v2',
      name: 'Elevator Pros',
      specialty: 'Elevators',
      rating: 4.8,
      lastService: '2024-02-15',
      cost: 3500
    }
  ]);

  const [inventory, setInventory] = useState<InventoryItem[]>([
    {
      id: 'i1',
      name: 'HVAC Filters',
      quantity: 15,
      reorderPoint: 10,
      lastOrdered: '2024-01-15',
      supplier: 'TechFix Solutions',
      cost: 45
    },
    {
      id: 'i2',
      name: 'Motor Belts',
      quantity: 8,
      reorderPoint: 5,
      lastOrdered: '2024-02-01',
      supplier: 'Elevator Pros',
      cost: 120
    }
  ]);

  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [showNewVendorForm, setShowNewVendorForm] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);

  // Task Management
  const handleAddTask = (task: Omit<MaintenanceTask, 'id'>) => {
    const newTask = {
      ...task,
      id: `t${tasks.length + 1}`
    };
    setTasks([...tasks, newTask]);
    setShowNewTaskForm(false);
  };

  const handleUpdateTaskStatus = (taskId: string, status: MaintenanceTask['status']) => {
    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, status } : task
    ));
  };

  // Vendor Management
  const handleAddVendor = (vendor: Omit<Vendor, 'id'>) => {
    const newVendor = {
      ...vendor,
      id: `v${vendors.length + 1}`
    };
    setVendors([...vendors, newVendor]);
    setShowNewVendorForm(false);
  };

  // Inventory Management
  const handleOrderParts = (itemId: string, quantity: number) => {
    setInventory(inventory.map(item =>
      item.id === itemId ? {
        ...item,
        quantity: item.quantity + quantity,
        lastOrdered: new Date().toISOString().split('T')[0]
      } : item
    ));
    setShowOrderForm(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <h2 className="text-2xl font-bold">Maintenance Action Center</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setShowNewTaskForm(true)}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            <Plus className="w-5 h-5" />
            <span>New Task</span>
          </button>
          <button
            onClick={() => setShowNewVendorForm(true)}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            <Users className="w-5 h-5" />
            <span>Add Vendor</span>
          </button>
        </div>
      </div>

      {/* Task Management */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Predictive Maintenance Tasks</h3>
          <div className="space-y-4">
            {tasks.map(task => (
              <div
                key={task.id}
                className={`border rounded-lg p-4 cursor-pointer ${
                  selectedTask === task.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedTask(task.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{task.equipment}</h4>
                    <div className="text-sm text-gray-500">{task.location}</div>
                    <div className="text-sm">
                      Predicted Failure: {task.predictedFailure}
                    </div>
                    <div className="text-sm text-blue-600">
                      Daily Foot Traffic: {task.trafficData.dailyAverage.toLocaleString()} people
                      <span className={`ml-2 ${
                        task.trafficData.trend === 'increasing' ? 'text-red-500' :
                        task.trafficData.trend === 'decreasing' ? 'text-green-500' :
                        'text-gray-500'
                      }`}>
                        ({task.trafficData.trend})
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`px-2 py-1 rounded text-sm ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                    <div className="mt-2">
                      <select
                        value={task.status}
                        onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value as MaintenanceTask['status'])}
                        className="border rounded px-2 py-1"
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                </div>
                {selectedTask === task.id && (
                  <div className="mt-4 space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Assigned To:</span> {task.assignedTo}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Required Parts:</span> {task.parts.join(', ')}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Estimated Cost:</span> ${task.cost}
                    </div>
                    <div className="mt-4 bg-gray-50 p-3 rounded-lg">
                      <h5 className="font-medium mb-2">Traffic Impact Analysis</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-600">Weekly Total Traffic</div>
                          <div className="text-lg font-medium">{task.trafficData.weeklyTotal.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Peak Hours</div>
                          <div className="text-lg font-medium">{task.trafficData.peakHours}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Impact Score</div>
                          <div className="text-lg font-medium">{task.trafficData.impactScore}/10</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Usage Trend</div>
                          <div className="text-lg font-medium capitalize">{task.trafficData.trend}</div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-gray-600">
                        {task.trafficData.trend === 'increasing' 
                          ? 'Higher than normal foot traffic is accelerating wear and tear.'
                          : task.trafficData.trend === 'decreasing'
                          ? 'Reduced foot traffic suggests normal maintenance schedule.'
                          : 'Consistent foot traffic aligns with expected maintenance needs.'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Vendor Management */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Vendor Management</h3>
          <div className="space-y-4">
            {vendors.map(vendor => (
              <div key={vendor.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{vendor.name}</h4>
                    <div className="text-sm text-gray-500">{vendor.specialty}</div>
                    <div className="text-sm">
                      Last Service: {vendor.lastService}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center">
                      <span className="text-yellow-500">★</span>
                      <span className="ml-1">{vendor.rating}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      ${vendor.cost} total spent
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inventory Management */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Parts Inventory</h3>
        <div className="grid grid-cols-3 gap-4">
          {inventory.map(item => (
            <div key={item.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">{item.name}</h4>
                  <div className="text-sm text-gray-500">
                    Quantity: {item.quantity} / Reorder at: {item.reorderPoint}
                  </div>
                  <div className="text-sm">
                    Last Ordered: {item.lastOrdered}
                  </div>
                </div>
                <button
                  onClick={() => setShowOrderForm(true)}
                  className={`px-3 py-1 rounded text-sm ${
                    item.quantity <= item.reorderPoint
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {item.quantity <= item.reorderPoint ? 'Reorder Now' : 'Order More'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Forms */}
      {showNewTaskForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-xl font-semibold mb-4">New Maintenance Task</h3>
            {/* Add form fields here */}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowNewTaskForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Handle form submission
                  setShowNewTaskForm(false);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewVendorForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-xl font-semibold mb-4">Add New Vendor</h3>
            {/* Add form fields here */}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowNewVendorForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Handle form submission
                  setShowNewVendorForm(false);
                }}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Add Vendor
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-xl font-semibold mb-4">Order Parts</h3>
            {/* Add form fields here */}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowOrderForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Handle form submission
                  setShowOrderForm(false);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceActionCenter; 